import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Hexagon, X } from 'lucide-react';
import './BrandedDialog.css';

const BrandedDialogContext = createContext(null);
let externalApi = null;

export const showBrandedConfirm = async (options) => {
  if (externalApi?.confirm) return externalApi.confirm(options);
  return false;
};

export const showBrandedAlert = async (options) => {
  if (externalApi?.alert) return externalApi.alert(options);
};

export const showBrandedToast = (options) => {
  if (externalApi?.toast) externalApi.toast(options);
};

export const useBrandedDialog = () => {
  const context = useContext(BrandedDialogContext);
  if (!context) {
    throw new Error('useBrandedDialog must be used inside BrandedDialogProvider');
  }
  return context;
};

const normalizeOptions = (options, fallbackTitle) => {
  if (typeof options === 'string') {
    return { title: fallbackTitle, message: options };
  }
  return { title: fallbackTitle, ...(options || {}) };
};

export function BrandedDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [toasts, setToasts] = useState([]);
  const resolverRef = useRef(null);

  const closeDialog = useCallback((value) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolver) resolver(value);
  }, []);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setDialog({
      type: 'confirm',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: 'default',
      ...normalizeOptions(options, 'Confirm action'),
    });
  }), []);

  const alert = useCallback((options) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setDialog({
      type: 'alert',
      confirmLabel: 'OK',
      tone: 'default',
      ...normalizeOptions(options, 'Notice'),
    });
  }), []);

  const toast = useCallback((options) => {
    const next = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      tone: 'default',
      ...normalizeOptions(options, ''),
    };
    setToasts((prev) => [...prev, next].slice(-4));
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toastItem) => toastItem.id !== id));
  }, []);

  useEffect(() => {
    externalApi = { confirm, alert, toast };
    return () => {
      if (externalApi?.confirm === confirm) externalApi = null;
    };
  }, [confirm, alert, toast]);

  useEffect(() => {
    if (!toasts.length) return undefined;
    const timers = toasts.map((toastItem) => (
      window.setTimeout(() => dismissToast(toastItem.id), toastItem.duration || 4200)
    ));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [toasts, dismissToast]);

  const api = useMemo(() => ({ confirm, alert, toast }), [confirm, alert, toast]);

  return (
    <BrandedDialogContext.Provider value={api}>
      {children}
      {dialog && (
        <div className="branded-dialog-backdrop" role="presentation" onMouseDown={() => closeDialog(false)}>
          <div
            className={`branded-dialog-card branded-dialog-${dialog.tone || 'default'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="branded-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="branded-dialog-close" type="button" onClick={() => closeDialog(false)} aria-label="Close">
              <X size={18} />
            </button>
            <div className="branded-dialog-hexagon" aria-hidden="true">
              <Hexagon size={54} strokeWidth={1.6} />
            </div>
            <h2 id="branded-dialog-title">{dialog.title}</h2>
            {dialog.message && <p>{dialog.message}</p>}
            <div className="branded-dialog-actions">
              {dialog.type === 'confirm' && (
                <button type="button" className="branded-dialog-btn branded-dialog-btn-secondary" onClick={() => closeDialog(false)}>
                  {dialog.cancelLabel || 'Cancel'}
                </button>
              )}
              <button type="button" className="branded-dialog-btn branded-dialog-btn-primary" onClick={() => closeDialog(true)} autoFocus>
                {dialog.confirmLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
      {toasts.length > 0 && (
        <div className="branded-toast-stack" aria-live="polite" aria-atomic="false">
          {toasts.map((toastItem) => (
            <div key={toastItem.id} className={`branded-toast branded-toast-${toastItem.tone || 'default'}`}>
              <Hexagon size={22} strokeWidth={1.7} className="branded-toast-icon" aria-hidden="true" />
              <div>
                {toastItem.title && <strong>{toastItem.title}</strong>}
                {toastItem.message && <span>{toastItem.message}</span>}
              </div>
              <button type="button" onClick={() => dismissToast(toastItem.id)} aria-label="Dismiss notification">
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </BrandedDialogContext.Provider>
  );
}
